import axios from "axios";
import { appUrl } from "../../../../../../utils/config";

// ============================================================================
// PROJECT TABS APIs - All APIs for Project Tabs (System View / Overall View)
// ============================================================================

/**
 * Helper function to convert view type to numeric parameter
 * @param {string|number} viewType - 'systemView' or 'overallView' or 1 or 2
 * @returns {number} - 1 for overall view, 2 for system view
 */
export const getViewTypeParam = (viewType) => {
  if (typeof viewType === 'number') {
    return viewType; // Already numeric
  }
  if (viewType === "overallView" || viewType === 1) {
    return 1;
  }
  if (viewType === "systemView" || viewType === 2) {
    return 2;
  }
  // Default to overall view (1)
  return 1;
};

/**
 * Helper function to convert numeric parameter to view type string
 * @param {number} viewParam - 1 for overall view, 2 for system view
 * @returns {string} - 'systemView' or 'overallView'
 */
export const getViewTypeFromParam = (viewParam) => {
  if (viewParam === 1) {
    return "overallView";
  }
  if (viewParam === 2) {
    return "systemView";
  }
  // Default to overall view
  return "overallView";
};

// Fetch Project Tabs - Unified API (returns tabs, columns, and data in one response)
// Backend should return: { status: true, data: { tabs: [...] } }
// Each tab contains: { id, name, sections: [{ columns: [...], rows: [...] }] }
// viewParam: 1 for overall view, 2 for system view
// filters: { batchId: number (required), operation: string (optional, default: "All") }
export const fetchProjectTabsAPI = async (projectId, viewParam = 1, filters = {}) => {
  // Convert to numeric if string is passed
  const numericViewParam = getViewTypeParam(viewParam);
  
  // Extract filters
  const { batchId, operation = 'All' } = filters;
  
  // COMMENTED OUT - Real API call (uncomment when API is ready)
  // const apiBaseUrl = `${appUrl}/${localStorage.getItem("appName")}/${localStorage.getItem("network")}/${localStorage.getItem("collabType")}/${localStorage.getItem("roleName")}`;
  // try {
  //   // Prepare payload for POST request
  //   const payload = {
  //     projectId: projectId, // Project ID (required)
  //     viewType: numericViewParam, // 1 for overall view, 2 for system view
  //     batchId: batchId, // Required - Batch ID for batch-level tabs
  //     operation: operation || 'All' // Optional - Operation filter (default: "All")
  //   };
  //   
  //   console.log('========================================');
  //   console.log('📡 POST API: fetchProjectTabs');
  //   console.log('API Endpoint: POST /{appName}/{network}/{collabType}/{roleName}/fetchProjectTabs');
  //   console.log('Payload:', JSON.stringify(payload, null, 2));
  //   
  //   const response = await axios.post(`${apiBaseUrl}/fetchProjectTabs`, payload);
  //   
  //   if (response?.data?.status && response?.data?.data) {
  //     console.log('Response:', response.data);
  //     console.log('========================================');
  //     return response.data;
  //   }
  //   
  //   return { status: false, data: { tabs: [] }, message: 'No data returned' };
  // } catch (error) {
  //   console.error("Error fetching project tabs:", error);
  //   throw error;
  // }
  
  // Log the API call details (for debugging)
  console.log('========================================');
  console.log('📡 [MOCK] POST API: fetchProjectTabs');
  console.log('Project ID:', projectId);
  console.log('View Type:', numericViewParam, numericViewParam === 1 ? '(Overall View)' : '(System View)');
  console.log('Batch ID:', batchId);
  console.log('Operation:', operation);
  console.log('========================================');

  // FAKE/MOCK RESPONSE - Unified structure with tabs, columns, and data
  // Use numericViewParam: 1 = systemView, 2 = overallView
  // NOTE: Overall View (1) has fewer tabs, System View (2) has more tabs
  const mockResponse = {
    status: true,
    data: {
      tabs: numericViewParam === 1 
        ? [
            {
              id: 1,
              name: "Materials & Consumption",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Material Description", field: "materialDescription" },
                    { id: 3, name: "Sponsor Material ID", field: "sponsorMaterialID" },
                    { id: 4, name: "CMO Material ID", field: "cmoMaterialID" },
                    { id: 5, name: "CMO Batch Number", field: "cmoBatchNumber" },
                    { id: 6, name: "Target Quantity", field: "targetQuantity" },
                    { id: 7, name: "Actual Quantity", field: "actualQuantity" },
                    { id: 8, name: "UOM", field: "uom" },
                    { id: 9, name: "Quantity Variance (%)", field: "quantityVariance" }
                  ],
                  rows: [
                {
                  procedureNumber: "8.2.2",
                  materialDescription: "Ethyl Acetate",
                  sponsorMaterialID: "8857-1",
                  cmoMaterialID: "8857-2",
                  cmoBatchNumber: "9852",
                  targetQuantity: "2,173",
                  actualQuantity: "2,173",
                  uom: "L / KG",
                      quantityVariance: "1.02%"
                },
                {
                  procedureNumber: "8.3.2",
                  materialDescription: "Caffeine Monohydrate",
                  sponsorMaterialID: "64119-1",
                  cmoMaterialID: "64119-2",
                  cmoBatchNumber: "9851",
                  targetQuantity: "328",
                  actualQuantity: "328",
                  uom: "L / KG",
                      quantityVariance: "0.02%"
                },
                {
                  procedureNumber: "8.5.1",
                  materialDescription: "Ethyl Acetate",
                  sponsorMaterialID: "8857-1",
                  cmoMaterialID: "8857-2",
                  cmoBatchNumber: "9850",
                  targetQuantity: "853",
                  actualQuantity: "853",
                  uom: "L / KG",
                      quantityVariance: "4.26%"
                },
                {
                  procedureNumber: "8.6.1",
                  materialDescription: "Water",
                  sponsorMaterialID: "W-001",
                  cmoMaterialID: "W-002",
                  cmoBatchNumber: "9849",
                  targetQuantity: "1,500",
                  actualQuantity: "1,495",
                  uom: "L",
                  quantityVariance: "0.33%"
                }
              ]
            }
          ]
        },
            {
              id: 2,
              name: "CPPs",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Parameter", field: "parameter" },
                    { id: 3, name: "Set Point", field: "setPoint" },
                    { id: 4, name: "Range", field: "range" },
                    { id: 5, name: "Actual Value", field: "actualValue" },
                    { id: 6, name: "Time Series Data", field: "timeSeriesData" },
                    { id: 7, name: "UOM", field: "uom" }
                  ],
                  rows: [
                {
                  procedureNumber: "8.2.2",
                  parameter: "Temperature",
                  setPoint: "25°C",
                  range: "20-30°C",
                  actualValue: "25.5°C",
                  timeSeriesData: "View",
                      uom: "°C"
                },
                {
                  procedureNumber: "8.2.2",
                  parameter: "Pressure",
                  setPoint: "1.5 bar",
                  range: "1.0-2.0 bar",
                  actualValue: "1.52 bar",
                  timeSeriesData: "View",
                      uom: "bar"
                },
                {
                  procedureNumber: "8.3.2",
                  parameter: "Stirring Speed",
                  setPoint: "200 rpm",
                  range: "150-250 rpm",
                  actualValue: "205 rpm",
                  timeSeriesData: "View",
                      uom: "rpm"
                },
                {
                  procedureNumber: "8.5.1",
                  parameter: "pH",
                  setPoint: "7.0",
                  range: "6.5-7.5",
                  actualValue: "7.1",
                  timeSeriesData: "View",
                  uom: "pH"
                }
              ]
            }
          ]
        },
            {
              id: 3,
              name: "In-Process Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Method Number", field: "methodNumber" },
                    { id: 3, name: "Method Name", field: "methodName" },
                    { id: 4, name: "Sample ID", field: "sampleID" },
                    { id: 5, name: "Sample Description", field: "sampleDescription" },
                    { id: 6, name: "Specification Limit", field: "specificationLimit" },
                    { id: 7, name: "UOM", field: "uom" },
                    { id: 8, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 9, name: "Result Value", field: "resultValue" },
                    { id: 10, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 11, name: "COAs (Yes / No)", field: "coasYesNo" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                  methodNumber: "M-001",
                  methodName: "pH Test",
                  sampleID: "S-001",
                  sampleDescription: "Sample 1",
                  specificationLimit: "6.5-7.5",
                  uom: "pH",
                  analyticalReportNumber: "AR-001",
                  resultValue: "7.0",
                  resultApprovedRejected: "Approved",
                      coasYesNo: "Yes"
                },
                {
                  procedureNumber: "8.3.2",
                  methodNumber: "M-002",
                  methodName: "Viscosity Test",
                  sampleID: "S-002",
                  sampleDescription: "Sample 2",
                  specificationLimit: "10-50 cPs",
                  uom: "cPs",
                  analyticalReportNumber: "AR-002",
                  resultValue: "25.5",
                  resultApprovedRejected: "Approved",
                      coasYesNo: "Yes"
                },
                {
                  procedureNumber: "8.5.1",
                  methodNumber: "M-003",
                  methodName: "Moisture Content",
                  sampleID: "S-003",
                  sampleDescription: "Sample 3",
                  specificationLimit: "≤5%",
                  uom: "%",
                  analyticalReportNumber: "AR-003",
                  resultValue: "3.2%",
                  resultApprovedRejected: "Approved",
                  coasYesNo: "No"
                }
              ]
            }
          ]
        },
            {
              id: 4,
              name: "Information Only Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Method Number", field: "methodNumber" },
                    { id: 3, name: "Method Name", field: "methodName" },
                    { id: 4, name: "Sample ID", field: "sampleID" },
                    { id: 5, name: "Sample Description", field: "sampleDescription" },
                    { id: 6, name: "Specification Limit", field: "specificationLimit" },
                    { id: 7, name: "UOM", field: "uom" },
                    { id: 8, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 9, name: "Result Value", field: "resultValue" },
                    { id: 10, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 11, name: "CQAs (Yes / No)", field: "cqasYesNo" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      methodNumber: "M-004",
                      methodName: "Color Test",
                      sampleID: "S-004",
                      sampleDescription: "Sample 4",
                      specificationLimit: "Clear",
                      uom: "Visual",
                      analyticalReportNumber: "AR-004",
                      resultValue: "Clear",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "No"
                    },
                    {
                      procedureNumber: "8.3.2",
                      methodNumber: "M-005",
                      methodName: "Odor Test",
                      sampleID: "S-005",
                      sampleDescription: "Sample 5",
                      specificationLimit: "Odorless",
                      uom: "Visual",
                      analyticalReportNumber: "AR-005",
                      resultValue: "Odorless",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "No"
                    }
                  ]
                }
              ]
            },
            {
              id: 5,
              name: "Product Release Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Method Number", field: "methodNumber" },
                    { id: 2, name: "Method Name", field: "methodName" },
                    { id: 3, name: "Sample ID", field: "sampleID" },
                    { id: 4, name: "Sample Description", field: "sampleDescription" },
                    { id: 5, name: "Specification Limit", field: "specificationLimit" },
                    { id: 6, name: "UOM", field: "uom" },
                    { id: 7, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 8, name: "Result Value", field: "resultValue" },
                    { id: 9, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 10, name: "CQAs (Yes / No)", field: "cqasYesNo" }
                  ],
                  rows: [
                    {
                      methodNumber: "M-006",
                      methodName: "Assay",
                      sampleID: "S-006",
                      sampleDescription: "Final Product",
                      specificationLimit: "95-105%",
                      uom: "%",
                      analyticalReportNumber: "AR-006",
                      resultValue: "98.5%",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "Yes"
                    },
                    {
                      methodNumber: "M-007",
                      methodName: "Impurities",
                      sampleID: "S-007",
                      sampleDescription: "Final Product",
                      specificationLimit: "≤0.5%",
                      uom: "%",
                      analyticalReportNumber: "AR-007",
                      resultValue: "0.3%",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "Yes"
                    },
                    {
                      methodNumber: "M-008",
                      methodName: "Dissolution",
                      sampleID: "S-008",
                      sampleDescription: "Final Product",
                      specificationLimit: "≥80%",
                      uom: "%",
                      analyticalReportNumber: "AR-008",
                      resultValue: "85.2%",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "Yes"
                    }
                  ]
                }
              ]
            },
            {
              id: 6,
              name: "CQAs",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Method Number", field: "methodNumber" },
                    { id: 2, name: "Method Name", field: "methodName" },
                    { id: 3, name: "Sample ID", field: "sampleID" },
                    { id: 4, name: "Sample Description", field: "sampleDescription" },
                    { id: 5, name: "Specification Limit", field: "specificationLimit" },
                    { id: 6, name: "UOM", field: "uom" },
                    { id: 7, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 8, name: "Result Value", field: "resultValue" },
                    { id: 9, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" }
                  ],
                  rows: [
                    {
                      methodNumber: "M-009",
                      methodName: "Purity",
                      sampleID: "S-009",
                      sampleDescription: "CQA Sample",
                      specificationLimit: "≥99%",
                      uom: "%",
                      analyticalReportNumber: "AR-009",
                      resultValue: "99.2%",
                      resultApprovedRejected: "Approved"
                    },
                    {
                      methodNumber: "M-010",
                      methodName: "Potency",
                      sampleID: "S-010",
                      sampleDescription: "CQA Sample",
                      specificationLimit: "95-105%",
                      uom: "%",
                      analyticalReportNumber: "AR-010",
                      resultValue: "98.8%",
                      resultApprovedRejected: "Approved"
                    }
                  ]
                }
              ]
            },
            {
              id: 7,
              name: "Deviations",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Deviation Number", field: "deviationNumber" },
                    { id: 2, name: "Procedure Number", field: "procedureNumber" },
                    { id: 3, name: "Deviation Title", field: "deviationTitle" },
                    { id: 4, name: "Reporting Date", field: "reportingDate" },
                    { id: 5, name: "Initial Deviation Notification", field: "initialDeviationNotification" },
                    { id: 6, name: "Deviation Assessment Report", field: "deviationAssessmentReport" },
                    { id: 7, name: "Deviation Closure Report", field: "deviationClosureReport" },
                    { id: 8, name: "CMO Deviation Status", field: "cmoDeviationStatus" },
                    { id: 9, name: "Sponsor Deviation Status", field: "bmsDeviationStatus" }
                  ],
                  rows: [
                    {
                      deviationNumber: "DEV-001",
                      procedureNumber: "8.2.2",
                      deviationTitle: "Temperature Deviation",
                      reportingDate: "2024-01-15",
                      initialDeviationNotification: "Yes",
                      deviationAssessmentReport: "Yes",
                      deviationClosureReport: "Yes",
                      cmoDeviationStatus: "Closed",
                      bmsDeviationStatus: "Closed"
                    },
                    {
                      deviationNumber: "DEV-002",
                      procedureNumber: "8.3.2",
                      deviationTitle: "Pressure Fluctuation",
                      reportingDate: "2024-01-20",
                      initialDeviationNotification: "Yes",
                      deviationAssessmentReport: "Yes",
                      deviationClosureReport: "Pending",
                      cmoDeviationStatus: "Open",
                      bmsDeviationStatus: "Under Review"
                    }
                  ]
                }
              ]
            },
            {
              id: 8,
              name: "Change Controls",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Change Control Title", field: "changeControlTitle" },
                    { id: 3, name: "Change Control Type", field: "changeControlType" },
                    { id: 4, name: "Change Control Number", field: "changeControlNumber" },
                    { id: 5, name: "Initial Procedure Number", field: "initialProcedureNumber" },
                    { id: 6, name: "Final Procedure Number", field: "finalProcedureNumber" },
                    { id: 7, name: "Initial Change Control Description", field: "initialChangeControlDescription" },
                    { id: 8, name: "Change Control Report", field: "changeControlReport" },
                    { id: 9, name: "CMO Change Control Status", field: "cmoChangeControlStatus" },
                    { id: 10, name: "Affected Batches", field: "affectedBatches" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      changeControlTitle: "Process Update",
                      changeControlType: "Minor",
                      changeControlNumber: "CC-001",
                      initialProcedureNumber: "8.2.1",
                      finalProcedureNumber: "8.2.2",
                      initialChangeControlDescription: "Updated temperature range",
                      changeControlReport: "Yes",
                      cmoChangeControlStatus: "Approved",
                      affectedBatches: "Batch 1, Batch 2"
                    },
                    {
                      procedureNumber: "8.5.1",
                      changeControlTitle: "Equipment Upgrade",
                      changeControlType: "Major",
                      changeControlNumber: "CC-002",
                      initialProcedureNumber: "8.5.0",
                      finalProcedureNumber: "8.5.1",
                      initialChangeControlDescription: "Upgraded reactor system",
                      changeControlReport: "Yes",
                      cmoChangeControlStatus: "Pending",
                      affectedBatches: "Batch 3, Batch 4"
                    }
                  ]
                }
              ]
            },
            {
              id: 9,
              name: "Cycle Time",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Target Start Time", field: "targetStartTime" },
                    { id: 3, name: "Actual Start Time", field: "actualStartTime" },
                    { id: 4, name: "Target End Time", field: "targetEndTime" },
                    { id: 5, name: "Actual End Time", field: "actualEndTime" },
                    { id: 6, name: "Planned Cycle Time", field: "plannedCycleTime" },
                    { id: 7, name: "Actual Cycle Time", field: "actualCycleTime" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      targetStartTime: "09:00",
                      actualStartTime: "09:05",
                      targetEndTime: "17:00",
                      actualEndTime: "17:10",
                      plannedCycleTime: "8 hours",
                      actualCycleTime: "8.08 hours"
                    },
                    {
                      procedureNumber: "8.3.2",
                      targetStartTime: "09:00",
                      actualStartTime: "09:02",
                      targetEndTime: "15:00",
                      actualEndTime: "15:05",
                      plannedCycleTime: "6 hours",
                      actualCycleTime: "6.05 hours"
                    },
                    {
                      procedureNumber: "8.5.1",
                      targetStartTime: "10:00",
                      actualStartTime: "10:10",
                      targetEndTime: "18:00",
                      actualEndTime: "18:15",
                      plannedCycleTime: "8 hours",
                      actualCycleTime: "8.08 hours"
                    }
                  ]
                }
              ]
            },
            {
              id: 10,
              name: "Equipment",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Equipment ID", field: "equipmentID" },
                    { id: 2, name: "Equipment Type", field: "equipmentType" },
                    { id: 3, name: "Equipment Specification-1", field: "equipmentSpecification1" },
                    { id: 4, name: "Equipment Specification-2", field: "equipmentSpecification2" },
                    { id: 5, name: "Equipment Specification-3", field: "equipmentSpecification3" },
                    { id: 6, name: "Location", field: "location" }
                  ],
                  rows: [
                    {
                      equipmentID: "EQ-001",
                      equipmentType: "Reactor",
                      equipmentSpecification1: "100L",
                      equipmentSpecification2: "Stainless Steel",
                      equipmentSpecification3: "Jacketed",
                      location: "Room 101"
                    },
                    {
                      equipmentID: "EQ-002",
                      equipmentType: "Mixer",
                      equipmentSpecification1: "50L",
                      equipmentSpecification2: "Glass Lined",
                      equipmentSpecification3: "Variable Speed",
                      location: "Room 102"
                    },
                    {
                      equipmentID: "EQ-003",
                      equipmentType: "Filter Press",
                      equipmentSpecification1: "200L",
                      equipmentSpecification2: "Stainless Steel",
                      equipmentSpecification3: "Plate and Frame",
                      location: "Room 103"
                    }
                  ]
                }
              ]
            },
            {
              id: 11,
              name: "Yield",
              sections: [
                {
                  columns: [
                    { id: 2, name: "Planned Product Quantity", field: "plannedProductQuantity" },
                    { id: 3, name: "Actual Product Quantity", field: "actualProductQuantity" },
                    { id: 4, name: "UOM", field: "uom" },
                    { id: 5, name: "Yield (%)", field: "yield" },
                    { id: 8, name: "Batch Manufacturing Date", field: "batchManufacturingDate" },
                    { id: 9, name: "Expiry Date", field: "expiryDate" },
                    { id: 6, name: "Storage Condition", field: "storageCondition" },
                    { id: 7, name: "Storage Location", field: "storageLocation" }
                  ],
                  rows: [
                    {
                      plannedProductQuantity: "800",
                      actualProductQuantity: "782",
                      uom: "KG",
                      yield: "98.75%",
                      batchManufacturingDate: "2024-01-15",
                      expiryDate: "2026-01-15",
                      storageCondition: "Ambient",
                      storageLocation: "Hyderabad, Telangana, India"
                    }
                  ]
                },
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 10, name: "Material Description", field: "materialDescription" },
                    { id: 11, name: "Sponsor Material ID", field: "sponsorMaterialID" },
                    { id: 12, name: "CMO Material ID", field: "cmoMaterialID" },
                    { id: 13, name: "CMO Batch Number", field: "cmoBatchNumber" },
                    { id: 14, name: "Inlet ID", field: "inletID" },
                    { id: 15, name: "Target Quantity", field: "targetQuantity" },
                    { id: 16, name: "Actual Quantity", field: "actualQuantity" },
                    { id: 17, name: "UOM", field: "uom" },
                    { id: 18, name: "Quantity Variance (%)", field: "quantityVariance" }
                  ],
                  rows: [
                    {
                      procedureNumber: "B.2.2",
                      materialDescription: "Ethyl Acetate",
                      sponsorMaterialID: "8857-1",
                      cmoMaterialID: "8857-2",
                      cmoBatchNumber: "9852",
                      inletID: "B-IN-1",
                      targetQuantity: "2,173",
                      actualQuantity: "2,173",
                      uom: "L / KG",
                      quantityVariance: "1.02%"
                    },
                    {
                      procedureNumber: "B.3.2",
                      materialDescription: "Caffeine Monohydrate",
                      sponsorMaterialID: "64119-1",
                      cmoMaterialID: "64119-2",
                      cmoBatchNumber: "9851",
                      inletID: "B-IN-2",
                      targetQuantity: "328",
                      actualQuantity: "328",
                      uom: "L / KG",
                      quantityVariance: "0.02%"
                    }
                  ]
                }
              ]
            },
            {
              id: 12,
              name: "Documents",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Document Type", field: "documentType" },
                    { id: 2, name: "File Name", field: "fileName" },
                    { id: 3, name: "Upload Date", field: "uploadDate" }
                  ],
                  rows: [
                    {
                      documentType: "Batch Record",
                      fileName: "BR-001.pdf",
                      uploadDate: "2024-01-15"
                    },
                    {
                      documentType: "COA",
                      fileName: "COA-001.pdf",
                      uploadDate: "2024-01-16"
                    },
                    {
                      documentType: "Protocol",
                      fileName: "PROT-001.pdf",
                      uploadDate: "2024-01-17"
                    },
                    {
                      documentType: "Report",
                      fileName: "RPT-001.pdf",
                      uploadDate: "2024-01-18"
                    }
                  ]
                }
              ]
            }
          ]
        : [
            {
              id: 1,
              name: "Materials & Consumption",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Material Description", field: "materialDescription" },
                    { id: 3, name: "Sponsor Material ID", field: "sponsorMaterialID" },
                    { id: 4, name: "CMO Material ID", field: "cmoMaterialID" },
                    { id: 5, name: "CMO Batch Number", field: "cmoBatchNumber" },
                    { id: 6, name: "Target Quantity", field: "targetQuantity" },
                    { id: 7, name: "Actual Quantity", field: "actualQuantity" },
                    { id: 8, name: "UOM", field: "uom" },
                    { id: 9, name: "Quantity Variance (%)", field: "quantityVariance" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      materialDescription: "Ethyl Acetate",
                      sponsorMaterialID: "8857-1",
                      cmoMaterialID: "8857-2",
                      cmoBatchNumber: "9852",
                      targetQuantity: "2,173",
                      actualQuantity: "2,173",
                      uom: "L / KG",
                      quantityVariance: "1.02%"
                    },
                    {
                      procedureNumber: "8.3.2",
                      materialDescription: "Caffeine Monohydrate",
                      sponsorMaterialID: "64119-1",
                      cmoMaterialID: "64119-2",
                      cmoBatchNumber: "9851",
                      targetQuantity: "328",
                      actualQuantity: "328",
                      uom: "L / KG",
                      quantityVariance: "0.02%"
                    }
                  ]
                }
              ]
            },
            {
              id: 3,
              name: "In-Process Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Method Number", field: "methodNumber" },
                    { id: 3, name: "Method Name", field: "methodName" },
                    { id: 4, name: "Sample ID", field: "sampleID" },
                    { id: 5, name: "Sample Description", field: "sampleDescription" },
                    { id: 6, name: "Specification Limit", field: "specificationLimit" },
                    { id: 7, name: "UOM", field: "uom" },
                    { id: 8, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 9, name: "Result Value", field: "resultValue" },
                    { id: 10, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 11, name: "COAs (Yes / No)", field: "coasYesNo" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      methodNumber: "M-001",
                      methodName: "pH Test",
                      sampleID: "S-001",
                      sampleDescription: "Sample 1",
                      specificationLimit: "6.5-7.5",
                      uom: "pH",
                      analyticalReportNumber: "AR-001",
                      resultValue: "7.0",
                      resultApprovedRejected: "Approved",
                      coasYesNo: "Yes"
                    }
                  ]
                }
              ]
            },
            {
              id: 4,
              name: "Information Only Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Method Number", field: "methodNumber" },
                    { id: 3, name: "Method Name", field: "methodName" },
                    { id: 4, name: "Sample ID", field: "sampleID" },
                    { id: 5, name: "Sample Description", field: "sampleDescription" },
                    { id: 6, name: "Specification Limit", field: "specificationLimit" },
                    { id: 7, name: "UOM", field: "uom" },
                    { id: 8, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 9, name: "Result Value", field: "resultValue" },
                    { id: 10, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 11, name: "CQAs (Yes / No)", field: "cqasYesNo" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      methodNumber: "M-004",
                      methodName: "Color Test",
                      sampleID: "S-004",
                      sampleDescription: "Sample 4",
                      specificationLimit: "Clear",
                      uom: "Visual",
                      analyticalReportNumber: "AR-004",
                      resultValue: "Clear",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "No"
                    }
                  ]
                }
              ]
            },
            {
              id: 5,
              name: "Product Release Tests",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Method Number", field: "methodNumber" },
                    { id: 2, name: "Method Name", field: "methodName" },
                    { id: 3, name: "Sample ID", field: "sampleID" },
                    { id: 4, name: "Sample Description", field: "sampleDescription" },
                    { id: 5, name: "Specification Limit", field: "specificationLimit" },
                    { id: 6, name: "UOM", field: "uom" },
                    { id: 7, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 8, name: "Result Value", field: "resultValue" },
                    { id: 9, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" },
                    { id: 10, name: "CQAs (Yes / No)", field: "cqasYesNo" }
                  ],
                  rows: [
                    {
                      methodNumber: "M-006",
                      methodName: "Assay",
                      sampleID: "S-006",
                      sampleDescription: "Final Product",
                      specificationLimit: "95-105%",
                      uom: "%",
                      analyticalReportNumber: "AR-006",
                      resultValue: "98.5%",
                      resultApprovedRejected: "Approved",
                      cqasYesNo: "Yes"
                    }
                  ]
                }
              ]
            },
            {
              id: 6,
              name: "CQAs",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Method Number", field: "methodNumber" },
                    { id: 2, name: "Method Name", field: "methodName" },
                    { id: 3, name: "Sample ID", field: "sampleID" },
                    { id: 4, name: "Sample Description", field: "sampleDescription" },
                    { id: 5, name: "Specification Limit", field: "specificationLimit" },
                    { id: 6, name: "UOM", field: "uom" },
                    { id: 7, name: "Analytical Report Number", field: "analyticalReportNumber" },
                    { id: 8, name: "Result Value", field: "resultValue" },
                    { id: 9, name: "Result (Approved / Rejected)", field: "resultApprovedRejected" }
                  ],
                  rows: [
                    {
                      methodNumber: "M-009",
                      methodName: "Purity",
                      sampleID: "S-009",
                      sampleDescription: "CQA Sample",
                      specificationLimit: "≥99%",
                      uom: "%",
                      analyticalReportNumber: "AR-009",
                      resultValue: "99.2%",
                      resultApprovedRejected: "Approved"
                    }
                  ]
                }
              ]
            },
            {
              id: 7,
              name: "Deviations",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Deviation Number", field: "deviationNumber" },
                    { id: 2, name: "Procedure Number", field: "procedureNumber" },
                    { id: 3, name: "Deviation Title", field: "deviationTitle" },
                    { id: 4, name: "Reporting Date", field: "reportingDate" },
                    { id: 5, name: "Initial Deviation Notification", field: "initialDeviationNotification" },
                    { id: 6, name: "Deviation Assessment Report", field: "deviationAssessmentReport" },
                    { id: 7, name: "Deviation Closure Report", field: "deviationClosureReport" },
                    { id: 8, name: "CMO Deviation Status", field: "cmoDeviationStatus" },
                    { id: 9, name: "Sponsor Deviation Status", field: "bmsDeviationStatus" }
                  ],
                  rows: [
                    {
                      deviationNumber: "DEV-001",
                      procedureNumber: "8.2.2",
                      deviationTitle: "Temperature Deviation",
                      reportingDate: "2024-01-15",
                      initialDeviationNotification: "Yes",
                      deviationAssessmentReport: "Yes",
                      deviationClosureReport: "Yes",
                      cmoDeviationStatus: "Closed",
                      bmsDeviationStatus: "Closed"
                    }
                  ]
                }
              ]
            },
            {
              id: 8,
              name: "Change Controls",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Procedure Number", field: "procedureNumber" },
                    { id: 2, name: "Change Control Title", field: "changeControlTitle" },
                    { id: 3, name: "Change Control Type", field: "changeControlType" },
                    { id: 4, name: "Change Control Number", field: "changeControlNumber" },
                    { id: 5, name: "Initial Procedure Number", field: "initialProcedureNumber" },
                    { id: 6, name: "Final Procedure Number", field: "finalProcedureNumber" },
                    { id: 7, name: "Initial Change Control Description", field: "initialChangeControlDescription" },
                    { id: 8, name: "Change Control Report", field: "changeControlReport" },
                    { id: 9, name: "CMO Change Control Status", field: "cmoChangeControlStatus" },
                    { id: 10, name: "Affected Batches", field: "affectedBatches" }
                  ],
                  rows: [
                    {
                      procedureNumber: "8.2.2",
                      changeControlTitle: "Process Update",
                      changeControlType: "Minor",
                      changeControlNumber: "CC-001",
                      initialProcedureNumber: "8.2.1",
                      finalProcedureNumber: "8.2.2",
                      initialChangeControlDescription: "Updated temperature range",
                      changeControlReport: "Yes",
                      cmoChangeControlStatus: "Approved",
                      affectedBatches: "Batch 1, Batch 2"
                    }
                  ]
                }
              ]
            },
            {
              id: 10,
              name: "Equipment",
              sections: [
                {
                  columns: [
                    { id: 1, name: "Equipment ID", field: "equipmentID" },
                    { id: 2, name: "Equipment Type", field: "equipmentType" },
                    { id: 3, name: "Equipment Specification-1", field: "equipmentSpecification1" },
                    { id: 4, name: "Equipment Specification-2", field: "equipmentSpecification2" },
                    { id: 5, name: "Equipment Specification-3", field: "equipmentSpecification3" },
                    { id: 6, name: "Location", field: "location" }
                  ],
                  rows: [
                    {
                      equipmentID: "EQ-001",
                      equipmentType: "Reactor",
                      equipmentSpecification1: "100L",
                      equipmentSpecification2: "Stainless Steel",
                      equipmentSpecification3: "Jacketed",
                      location: "Room 101"
                    }
                  ]
                }
              ]
            }
          ]
    },
    message: "Success"
  };

  console.log("[FAKE API] fetchProjectTabsAPI Response:", mockResponse);
  return mockResponse;
};

// DEPRECATED: Keep for backward compatibility, but use fetchProjectTabsAPI instead
export const fetchProjectTabsConfigAPI = async (projectId, viewParam = 1) => {
  console.warn("[DEPRECATED] fetchProjectTabsConfigAPI is deprecated. Use fetchProjectTabsAPI instead.");
  return fetchProjectTabsAPI(projectId, viewParam);
};

// DEPRECATED: Use fetchProjectTabsAPI instead - kept for backward compatibility
export const fetchAllTabsDataAPI = async (projectId, viewParam = 1, filters = {}) => {
  console.warn("[DEPRECATED] fetchAllTabsDataAPI is deprecated. Use fetchProjectTabsAPI instead.");
  const response = await fetchProjectTabsAPI(projectId, viewParam);
  // Transform to old format for backward compatibility
  if (response.status && response.data && response.data.tabs) {
    const tabsData = {};
    response.data.tabs.forEach(tab => {
      tabsData[tab.name] = {
        tables: tab.sections // Map sections to tables for backward compatibility
      };
    });
    return {
      status: true,
      data: { tabsData },
      message: response.message
    };
  }
  return response;
};

// DEPRECATED: Use fetchAllTabsDataAPI instead - kept for backward compatibility
// Fetch Tab Data for a specific tab
// viewParam: 1 for overall view, 2 for system view
export const fetchTabDataAPI = async (projectId, tabName, viewParam = 1, filters = {}) => {
  // Convert to numeric if string is passed
  const numericViewParam = getViewTypeParam(viewParam);
  
  const apiBaseUrl = `${appUrl}/${localStorage.getItem("appName")}/${localStorage.getItem("network")}/${localStorage.getItem("collabType")}/${localStorage.getItem("roleName")}`;
  try {
    const response = await axios.get(`${apiBaseUrl}/fetchTabData/${projectId}/${encodeURIComponent(tabName)}`, { 
      params: { viewType: numericViewParam, ...filters } 
    });
    
    // Backend response structure should be:
    // {
    //   status: true,
    //   data: {
    //     tabName: "Materials & Consumption",
    //     tables: [
    //       {
    //         data: [
    //           {
    //             procedureNumber: "8.2.2",
    //             materialDescription: "Ethyl Acetate",
    //             // ... other fields matching column field names from column config
    //           }
    //         ]
    //       }
    //     ]
    //   },
    //   message: "Tab data fetched successfully"
    // }
    // OR for multiple tables (Yield):
    // {
    //   status: true,
    //   data: {
    //     tabName: "Yield",
    //     tables: [
    //       {
    //         tableType: "summary",
    //         title: "Batch Yield Summary", // optional
    //         data: [...]
    //       },
    //       {
    //         tableType: "materials",
    //         title: "Materials Consumption", // optional
    //         data: [...]
    //       }
    //     ]
    //   }
    // }
    
    // Return the response data - backend should return the structure above
    // If backend wraps it differently, adjust here
    console.log(`[API] fetchTabDataAPI response for ${tabName}:`, response);
    
    // Handle different response structures
    if (response.data) {
      // Case 1: Backend returns { status, data: { tabName, tables: [...] }, message }
      if (response.data.data && response.data.data.tabName) {
        console.log(`[API] Using response.data.data structure`);
        return response.data;
      }
      // Case 2: Backend returns { tabName, tables: [...] } directly in response.data
      if (response.data.tabName || response.data.tables) {
        console.log(`[API] Using response.data structure directly`);
        return { 
          status: true, 
          data: response.data, 
          message: "Success" 
        };
      }
      // Case 3: Backend returns data array directly
      if (Array.isArray(response.data)) {
        console.log(`[API] Using response.data as array, wrapping in tables structure`);
        return {
          status: true,
          data: {
            tabName: tabName,
            tables: [{ data: response.data }],
          },
          message: "Success",
        };
      }
    }
    
    // Fallback: return empty structure
    console.warn(`[API] No valid data structure found for ${tabName}, returning empty`);
    return {
      status: true,
      data: {
        tabName: tabName,
        tables: [{ data: [] }],
      },
      message: "No data returned",
    };
  } catch (error) {
    console.error("Error fetching tab data:", error);
    // Return empty data structure on error - table will still render with "No data available"
    return {
      status: false,
      data: {
        tabName: tabName,
        tables: [
          {
            data: [],
          },
        ],
      },
      message: "Error fetching tab data",
    };
  }
};

